import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isIntercomEnabled, showIntercomMessenger } from '@/lib/intercom';
import { useCloseNotificationDetail, useCloseNotificationsPanel } from '@/store/notificationsStore';
import { marketDeepLinkId } from './meta';
import type { PopupAction } from './popup-cta';
import type { NotificationResponseDto } from '@/lib/models';

/** The lobby (home) route — where `navigateLobby` lands. */
const LOBBY_ROUTE = '/';
/**
 * Support fallback when Intercom isn't configured for the build: the Settings
 * page hosts the "Support" widget (which itself opens Intercom when enabled).
 * `showIntercomMessenger` is a safe no-op when disabled, so this keeps the CTA
 * from dead-ending.
 */
const SUPPORT_FALLBACK_ROUTE = '/settings';

/** Runs a resolved CTA action for a given notification. */
export type CtaRunner = (action: PopupAction, notification: NotificationResponseDto) => void;

/**
 * Action router for the notification pop-up CTAs. Turns a {@link PopupAction}
 * into the concrete side effect, tearing down the right amount of surface:
 *
 *  - `dismiss`        — close just the pop-up (the panel stays where it was).
 *  - `navigateLobby`  — close pop-up + panel, then go to the lobby.
 *  - `navigateMarket` — close pop-up + panel, then open the market/event page
 *    (`meta.eventId` / `meta.marketId`). With no deep-link id it degrades to a
 *    plain dismiss rather than navigating nowhere.
 *  - `openIntercom`   — close pop-up + panel, then open the support messenger
 *    (falls back to the Settings support surface when Intercom is disabled).
 *
 * Navigation tears down the panel too: on mobile it sits open *under* the sheet
 * and would otherwise cover the destination (same reasoning as the legacy
 * "View market" link).
 */
export function useCtaRouter(): CtaRunner {
  const navigate = useNavigate();
  const closeDetail = useCloseNotificationDetail();
  const closePanel = useCloseNotificationsPanel();

  return useCallback(
    (action, notification) => {
      switch (action) {
        case 'dismiss':
          closeDetail();
          return;
        case 'navigateLobby':
          closeDetail();
          closePanel();
          navigate(LOBBY_ROUTE);
          return;
        case 'navigateMarket': {
          const id = marketDeepLinkId(notification.meta);
          closeDetail();
          closePanel();
          if (id) navigate(`/events/${id}`);
          return;
        }
        case 'openIntercom':
          closeDetail();
          closePanel();
          if (isIntercomEnabled()) showIntercomMessenger();
          else navigate(SUPPORT_FALLBACK_ROUTE);
          return;
      }
    },
    [navigate, closeDetail, closePanel],
  );
}
